import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dices } from 'lucide-react';
import { cn } from '../../lib/utils';
import { fadeDiceRoll, stopDiceRoll } from './diceBoxEngine';

const LIVE_ADD_DICE = [4, 6, 8, 10, 12, 20, 100];

const CritParticles = () => {
  const particles = useMemo(() => (
    Array.from({ length: 20 }, (_, index) => ({
      id: index,
      angle: (index / 20) * 360,
      distance: 60 + Math.random() * 120,
      size: 4 + Math.random() * 6,
      delay: Math.random() * 0.3,
      duration: 0.6 + Math.random() * 0.6,
    }))
  ), []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="dice3d-particle"
          style={{
            left: '50%',
            top: '50%',
            width: particle.size,
            height: particle.size,
            '--tx': `${Math.cos((particle.angle * Math.PI) / 180) * particle.distance}px`,
            '--ty': `${Math.sin((particle.angle * Math.PI) / 180) * particle.distance}px`,
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

const DiceResults = ({ rollData }) => {
  const dice = useMemo(() => (
    (rollData?.rolls || []).flatMap((group) => {
      const keptCounts = (group.kept || []).reduce((counts, value) => ({
        ...counts,
        [value]: (counts[value] || 0) + 1,
      }), {});
      const matchedCounts = {};

      return (group.rolls || []).map((value) => {
        matchedCounts[value] = (matchedCounts[value] || 0) + 1;
        return {
          value,
          sides: group.die_size,
          dropped: matchedCounts[value] > (keptCounts[value] || 0),
        };
      });
    })
  ), [rollData]);

  return (
    <div className="mb-5 flex max-w-[min(90vw,720px)] flex-wrap items-center justify-center gap-2.5">
      {dice.map((die, index) => (
        <div
          key={`${die.sides}-${index}`}
          className={cn(
            'dice3d-result-chip',
            die.dropped && 'opacity-30 line-through grayscale'
          )}
          style={{
            animationDelay: `${index * 45}ms`,
            '--dice-rotation': `${index % 2 === 0 ? -10 : 10}deg`,
          }}
        >
          <span className="text-[9px] uppercase tracking-wider text-white/50">d{die.sides}</span>
          <span className="text-xl font-bold leading-none tabular-nums text-white">{die.value}</span>
        </div>
      ))}
    </div>
  );
};

const Dice3DOverlay = ({
  rollData,
  pending = false,
  pendingDiceCount = 0,
  pendingNotation = '',
  onAddDice,
  onComplete,
}) => {
  const [visible, setVisible] = useState(true);
  const [showTotal, setShowTotal] = useState(false);
  const onCompleteRef = useRef(onComplete);
  const dismissedRef = useRef(false);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const dismiss = useCallback(() => {
    if (pending || dismissedRef.current) return;
    dismissedRef.current = true;
    setVisible(false);
    stopDiceRoll();
    window.setTimeout(() => onCompleteRef.current?.(), 300);
  }, [pending]);

  useEffect(() => {
    let totalTimer;
    let dismissTimer;

    dismissedRef.current = false;
    setVisible(true);

    if (pending || !rollData) {
      setShowTotal(false);
      return undefined;
    }

    // Local rolls leave their exact physical faces visible until the API has
    // calculated them. Remote views animate these same recorded face values.
    fadeDiceRoll();
    setShowTotal(false);
    totalTimer = window.setTimeout(() => setShowTotal(true), 520);
    dismissTimer = window.setTimeout(dismiss, 4200);

    return () => {
      window.clearTimeout(totalTimer);
      window.clearTimeout(dismissTimer);
    };
  }, [dismiss, pending, rollData]);

  useEffect(() => () => stopDiceRoll(), []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!pending && (event.key === 'Escape' || event.key === ' ')) {
        event.preventDefault();
        dismiss();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dismiss, pending]);

  if (!pending && !rollData?.rolls?.length) return null;

  const isCrit = rollData?.is_critical;
  const isFumble = rollData?.is_fumble;
  const showResult = !pending && Boolean(rollData);

  return (
    <Fragment>
      <button
        type="button"
        aria-label="Fermer l'animation du lancer"
        disabled={pending}
        className={cn(
          'fixed inset-0 z-[9998] border-0 transition-opacity duration-300',
          pending ? 'pointer-events-none cursor-wait' : 'cursor-pointer',
          isCrit ? 'bg-yellow-950/75' : isFumble ? 'bg-red-950/75' : 'bg-black/70',
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={dismiss}
      />

      <div
        className={cn(
          'pointer-events-none fixed inset-0 z-[10001] flex flex-col items-center justify-center px-4 transition-opacity duration-300',
          visible ? 'opacity-100' : 'opacity-0'
        )}
        role="status"
        aria-live="polite"
      >
        {isCrit && showResult && <CritParticles />}

        {!showResult && (
          <div className="pointer-events-auto absolute bottom-8 left-1/2 flex max-w-[94vw] -translate-x-1/2 flex-col items-center gap-2 rounded-2xl border border-white/10 bg-black/55 px-5 py-3 text-xs text-white/75 shadow-2xl backdrop-blur-sm">
            <span className="flex items-center gap-2">
              <Dices className="h-4 w-4 animate-pulse" />
              Lancer physique en cours · {pendingDiceCount} {pendingDiceCount > 1 ? 'dés' : 'dé'}
            </span>
            {pendingNotation && (
              <span className="max-w-[75vw] truncate font-mono text-[10px] text-white/45">
                {pendingNotation}
              </span>
            )}
            {onAddDice && (
              <div className="flex max-w-[88vw] flex-wrap items-center justify-center gap-1.5" aria-label="Ajouter des dés au lancer en cours">
                {LIVE_ADD_DICE.map((sides) => (
                  <button
                    key={sides}
                    type="button"
                    className="rounded-lg border border-violet-300/25 bg-violet-500/20 px-2.5 py-1.5 font-mono text-[11px] font-bold text-violet-100 transition hover:border-violet-200/50 hover:bg-violet-500/35 active:scale-95"
                    onClick={() => onAddDice(`1d${sides}`, `d${sides}`)}
                  >
                    + d{sides}
                  </button>
                ))}
              </div>
            )}
            <span className="text-[10px] text-violet-200/75">
              Ajoutez autant de dés que nécessaire avant la fin du lancer
            </span>
          </div>
        )}

        <div className={cn(
          'flex flex-col items-center text-center transition-all duration-500',
          showResult ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-5 scale-95 opacity-0'
        )}>
          {showResult && <DiceResults rollData={rollData} />}

          <div className={cn(
            'text-6xl font-bold font-cinzel tabular-nums transition-all duration-300 sm:text-7xl',
            showTotal ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
            isCrit ? 'text-yellow-400 dice3d-glow-gold' :
              isFumble ? 'text-red-400 dice3d-glow-red' :
                'text-white dice3d-glow-white'
          )}>
            {rollData?.total}
          </div>

          <p className="mt-1.5 max-w-[90vw] text-sm font-mono text-white/55">
            {rollData?.breakdown}
          </p>

          {rollData?.label && (
            <p className="mt-1 text-sm font-medium text-white/75">{rollData.label}</p>
          )}

          <div className="mt-3 flex items-center justify-center gap-2">
            {isCrit && (
              <span className="rounded-full border border-yellow-500/40 bg-yellow-500/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-yellow-300">
                Critique !
              </span>
            )}
            {isFumble && (
              <span className="rounded-full border border-red-500/40 bg-red-500/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-300">
                Fumble !
              </span>
            )}
            {rollData?.secret && (
              <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-white/60">
                Secret
              </span>
            )}
          </div>

          <p className="mt-6 text-[10px] text-white/35">Cliquer ou Échap pour fermer</p>
        </div>
      </div>
    </Fragment>
  );
};

export default Dice3DOverlay;
